/**
 * API Client - Central abstraction for mock vs real API switching
 * Provides a single gateway to prevent scattering environment checks
 */

import type {
  Community,
  Resident,
  CollateralAsset,
  BankPartner,
  CommunityLoanProposal,
  LoanProposalWithDetails,
  BankProposalQueue,
  RiskAssessment,
  PortfolioSummary,
  FinancialMetrics,
  ApiResponse,
  PaginatedResponse,
  GetProposalQueueParams,
  CreateLoanProposalParams,
  UpdateProposalStatusParams,
} from '@/types/api/lending';

import { mockLendingAPI } from './mockLendingAPI';

// ============================================================================
// ENVIRONMENT CONFIGURATION
// ============================================================================

const USE_MOCK_API =
  import.meta.env.VITE_USE_MOCK_API === 'true' ||
  import.meta.env.DEV ||
  !import.meta.env.VITE_API_BASE_URL;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

// ============================================================================
// REAL API CLIENT (Placeholder for future implementation)
// ============================================================================

class RealAPIClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw new Error(
        `API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { cause: error },
      );
    }
  }

  // Communities
  communities = {
    getAll: (): Promise<ApiResponse<Community[]>> => this.request<Community[]>('/communities'),

    getById: (id: string): Promise<ApiResponse<Community>> =>
      this.request<Community>(`/communities/${id}`),

    getFinancials: (communityId: string): Promise<ApiResponse<Record<string, number>>> =>
      this.request<Record<string, number>>(`/communities/${communityId}/financials`),
  };

  // Residents
  residents = {
    getByCommunity: (communityId: string): Promise<ApiResponse<Resident[]>> =>
      this.request<Resident[]>(`/communities/${communityId}/residents`),

    getById: (id: string): Promise<ApiResponse<Resident>> =>
      this.request<Resident>(`/residents/${id}`),
  };

  // Collateral
  collateral = {
    getByCommunity: (communityId: string): Promise<ApiResponse<CollateralAsset[]>> =>
      this.request<CollateralAsset[]>(`/communities/${communityId}/collateral`),

    getById: (id: string): Promise<ApiResponse<CollateralAsset>> =>
      this.request<CollateralAsset>(`/collateral/${id}`),
  };

  // Bank Operations
  bank = {
    getProposalQueue: (
      bankId: string,
      params?: GetProposalQueueParams,
    ): Promise<ApiResponse<BankProposalQueue>> => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, String(value));
          }
        });
      }
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      return this.request<BankProposalQueue>(`/banks/${bankId}/proposals${queryString}`);
    },

    getProposal: (proposalId: string): Promise<ApiResponse<LoanProposalWithDetails>> =>
      this.request<LoanProposalWithDetails>(`/proposals/${proposalId}`),

    getRiskAssessment: (proposalId: string): Promise<ApiResponse<RiskAssessment>> =>
      this.request<RiskAssessment>(`/proposals/${proposalId}/risk-assessment`),

    getPortfolioSummary: (bankId: string): Promise<ApiResponse<PortfolioSummary>> =>
      this.request<PortfolioSummary>(`/banks/${bankId}/portfolio/summary`),

    createProposal: (
      params: CreateLoanProposalParams,
    ): Promise<ApiResponse<CommunityLoanProposal>> =>
      this.request<CommunityLoanProposal>('/proposals', {
        method: 'POST',
        body: JSON.stringify(params),
      }),

    updateProposalStatus: (
      params: UpdateProposalStatusParams,
    ): Promise<ApiResponse<CommunityLoanProposal>> =>
      this.request<CommunityLoanProposal>(`/proposals/${params.proposal_id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(params),
      }),
  };

  // Financial Metrics
  financial = {
    getMetrics: (bankId: string): Promise<ApiResponse<FinancialMetrics>> =>
      this.request<FinancialMetrics>(`/banks/${bankId}/metrics`),
  };
}

// ============================================================================
// UNIFIED API CLIENT
// ============================================================================

class LendingAPIClient {
  private mockClient = mockLendingAPI;
  private realClient = new RealAPIClient(API_BASE_URL);
  private useMock = USE_MOCK_API;

  constructor(forceMock?: boolean) {
    if (forceMock !== undefined) {
      this.useMock = forceMock;
    }
  }

  // Allow runtime switching for testing/demo purposes
  setUseMock(useMock: boolean): void {
    this.useMock = useMock;
  }

  getEnvironmentInfo() {
    return {
      useMock: this.useMock,
      apiBaseUrl: API_BASE_URL,
      isDev: import.meta.env.DEV,
      viteUseMockApi: import.meta.env.VITE_USE_MOCK_API,
    };
  }

  // Communities
  get communities() {
    return this.useMock ? this.mockClient.communities : this.realClient.communities;
  }

  // Residents
  get residents() {
    return this.useMock ? this.mockClient.residents : this.realClient.residents;
  }

  // Collateral
  get collateral() {
    return this.useMock ? this.mockClient.collateral : this.realClient.collateral;
  }

  // Bank Operations
  get bank() {
    return this.useMock ? this.mockClient.bank : this.realClient.bank;
  }

  // Financial Metrics
  get financial() {
    return this.useMock ? this.mockClient.financial : this.realClient.financial;
  }
}

// ============================================================================
// DEFAULT EXPORT & SINGLETON
// ============================================================================

// Create a default singleton instance
export const apiClient = new LendingAPIClient();

// Export types for convenience
export type APIClient = LendingAPIClient;

// Named exports for specific use cases
export { mockLendingAPI, RealAPIClient, LendingAPIClient };

// Environment helpers
export const isUsingMockAPI = () => USE_MOCK_API;
export const getAPIBaseURL = () => API_BASE_URL;

export default apiClient;
